export interface GeoCoords {
  lat: number;
  lng: number;
  accuracy_m: number;
  address: string | null;
}

/**
 * Capture the device's current location. Resolves with null if:
 *   - Geolocation API unavailable
 *   - User denies permission
 *   - Lookup times out
 *
 * Address is left null for now; reverse geocoding can be added later
 * via a free service (e.g. Nominatim) without changing the call site.
 */
export async function captureGeolocation(timeoutMs = 8000): Promise<GeoCoords | null> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return null;
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v: GeoCoords | null) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        finish({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          address: null,
        });
      },
      () => {
        clearTimeout(timer);
        finish(null);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60_000 }
    );
  });
}

/**
 * Great-circle distance between two lat/lng points in metres (Haversine).
 * Accurate to ~0.5% for the small distances we care about.
 */
export function distanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const dPhi = ((lat2 - lat1) * Math.PI) / 180;
  const dLambda = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dPhi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
